// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import OcularMotilityCard from "./OcularMotilityCard/OcularMotilityCard";
import OcularMotilityFieldPlot from "./OcularMotilityFieldPlot/OcularMotilityFieldPlot";
import OcularMotilityTimeline from "./OcularMotilityTimeline/OcularMotilityTimeline";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Types
import { OcularMotilityResultsType } from "src/services/exams/types";
import { OcularMotilityPositionLayoutType } from "src/constants/enums";

// Utils
import { getOcularMotilityPositionKey } from "src/utils/exams/examReportsHelper";

type OcularMotilityPositionsGraphType = {
  results: OcularMotilityResultsType | undefined;
  cardLayout: Array<OcularMotilityPositionLayoutType>;
};

const signed = (value: number) =>
  `${value >= 0 ? "+" : ""}${value.toFixed(2)}°`;

// Shared by nine-gaze (all 9 compass positions) and H-pattern (the 7 of them
// it actually visits -- no pure Up or Down), since both post-process to the
// exact same results shape and name their positions after the same
// compass-direction keys -- only which positions the exam actually walked
// differs, so the caller tells us that via cardLayout.
const OcularMotilityPositionsGraph = ({
  results,
  cardLayout,
}: OcularMotilityPositionsGraphType) => {
  const { t } = useTranslation();
  const { examReport } = useContext(ReportSectionContext);

  const positionResults = results?.positionResults;
  const eyeImages = results?.eyeImages;
  const summary = results?.summary;

  const blinkEvents = examReport?.postProcessing?.blinkEvents ?? [];
  const timing = examReport?.timing;

  const cards = useMemo(
    () =>
      cardLayout.map((position) => ({
        ...position,
        result: positionResults?.find(
          (positionResult) =>
            getOcularMotilityPositionKey(positionResult.focalPointPosition) ===
            position.key
        ),
        image: eyeImages?.find(
          (image) =>
            getOcularMotilityPositionKey(image.focalPointPosition) ===
            position.key
        ),
      })),
    [positionResults, eyeImages, cardLayout]
  );

  if (!results) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
          {t("No Data Available.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      {summary && (
        <Box display="flex" flexDirection="column">
          <Typography fontSize="14px">
            <span style={{ fontWeight: 600 }}>
              {t("Primary Deviating Eye")}:
            </span>{" "}
            {summary.primaryDeviatingEye
              ? t(summary.primaryDeviatingEye)
              : t("Neither")}
          </Typography>
          <Typography fontSize="14px">
            <span style={{ fontWeight: 600 }}>
              {t("Primary Deviation Difference")}:
            </span>{" "}
            {signed(summary.primaryDeviationDifference)} {t("L - R")}
          </Typography>
        </Box>
      )}

      <Box display="flex" flexWrap="wrap" gap="12px">
        {cards.map((card, index) => (
          <OcularMotilityCard
            key={card.key}
            label={`${index + 1}. ${t(card.label)}`}
            subtitle={card.subtitle}
            result={card.result}
            image={card.image}
          />
        ))}
      </Box>

      <Box display="flex" flexDirection="column" gap="10px">
        <Typography fontSize="14px" fontWeight={600}>
          {t("Where the Eyes Actually Went")}
        </Typography>
        <OcularMotilityFieldPlot
          cardLayout={cardLayout}
          positionResults={positionResults ?? []}
        />
      </Box>

      {timing && (
        <Box display="flex" flexDirection="column" gap="16px">
          <Typography fontSize="14px" fontWeight={600}>
            {t("How the Exam Ran")}
          </Typography>
          <Box
            display="flex"
            flexWrap="wrap"
            gap="16px"
            color="#5B6D7A"
            fontSize="12px"
          >
            <Box display="flex" alignItems="center" gap="8px">
              <Box
                sx={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "#4C8DFF",
                }}
              />
              {t("Eye Position Duration")}
            </Box>
            <Box display="flex" alignItems="center" gap="8px">
              <Box
                sx={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "#6FCF97",
                }}
              />
              {t("Blink")}
            </Box>
          </Box>
          <OcularMotilityTimeline
            cardLayout={cardLayout}
            positionResults={positionResults ?? []}
            blinkEvents={blinkEvents}
            timing={timing}
          />
        </Box>
      )}
    </Box>
  );
};

export default OcularMotilityPositionsGraph;
